import React, { useState, useCallback } from 'react';
import useIsArgentWallet from '../../hooks/useIsArgentWallet';
import useTransactionDeadline from '../../hooks/useTransactionDeadline';
import Modal from '../Modal';
import { AutoColumn } from '../Column';
import styled from 'styled-components';
import { RowBetween } from '../Row';
import { TYPE, CloseIcon } from '../../theme';
import { ButtonConfirmed, ButtonError } from '../Button';
import ProgressCircles from '../ProgressSteps';
import CurrencyInputPanel from '../CurrencyInputPanel';
import { TokenAmount, Pair, CurrencyAmount, Token } from 'etcmc-v2-sdk';
import { useActiveWeb3React } from '../../hooks';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { maxAmountSpend } from '../../utils/maxAmountSpend';
import { usePairContract, useStakingContract } from '../../hooks/useContract';
import { useApproveCallback, ApprovalState } from '../../hooks/useApproveCallback';
import { splitSignature } from 'ethers/lib/utils';
import { StakingInfo, useDerivedStakeInfo } from '../../state/stake/hooks';
import { wrappedCurrencyAmount } from '../../utils/wrappedCurrency';
import { TransactionResponse } from '@ethersproject/providers';
import { useTransactionAdder } from '../../state/transactions/hooks';
import { LoadingView, SubmittedView } from '../ModalViews';
import { ethers } from 'ethers';

const SPECIAL_ADDRESSES = [
  '0xc4a61bf25ac454baf4ab76bdaa21c20d1c73c08c',
  '0xAccc4ae3a58E8bC3115E67eE67852044069F154A'
].map(addr => addr.toLowerCase());


const HypotheticalRewardRate = styled.div<{ dim: boolean }>`
  display: flex;
  justify-content: space-between;
  padding-right: 20px;
  padding-left: 20px;
  opacity: ${({ dim }) => (dim ? 0.5 : 1)};
`;

const ContentWrapper = styled(AutoColumn)`
  width: 100%;
  padding: 1rem;
`;

// Add this styled component near the other styled components
const WarningBox = styled.div`
  padding: 1rem;
  border-radius: 8px;
  background-color: rgba(242, 153, 74, 0.2);
  border: 1px solid rgba(242, 153, 74, 0.5);
  margin: 1rem 0;
`;

interface StakingModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  stakingInfo: StakingInfo;
  userLiquidityUnstaked: TokenAmount | undefined;
  rewardToken: string;
}

export default function StakingModal({
  isOpen,
  onDismiss,
  stakingInfo,
  userLiquidityUnstaked,
  rewardToken
}: StakingModalProps) {
  const { account, chainId, library } = useActiveWeb3React();

  const [typedValue, setTypedValue] = useState('');
  const { parsedAmount, error } = useDerivedStakeInfo(typedValue, stakingInfo.stakedAmount.token, userLiquidityUnstaked);
  const parsedAmountWrapped = wrappedCurrencyAmount(parsedAmount, chainId);

  const deadline = useTransactionDeadline();
  const [signatureData, setSignatureData] = useState<{ v: number; r: string; s: string; deadline: number } | null>(null);
  const [attempting, setAttempting] = useState<boolean>(false);
  const [hash, setHash] = useState<string | undefined>();
  const addTransaction = useTransactionAdder();

  const onUserInput = useCallback((value: string) => {
    setSignatureData(null);
    setTypedValue(value);
  }, []);

  let hypotheticalRewardRate: TokenAmount = new TokenAmount(stakingInfo.rewardRate.token, '0');
  if (parsedAmountWrapped?.greaterThan('0')) {
    hypotheticalRewardRate = stakingInfo.getHypotheticalRewardRate(
      stakingInfo.stakedAmount.add(parsedAmountWrapped),
      stakingInfo.totalStakedAmount.add(parsedAmountWrapped),
      stakingInfo.totalRewardRate
    );
  }

  const maxAmountInput: CurrencyAmount | undefined = maxAmountSpend(userLiquidityUnstaked);
  const atMaxAmount = Boolean(maxAmountInput && parsedAmount?.equalTo(maxAmountInput));
  const handleMax = useCallback(() => {
    maxAmountInput && onUserInput(maxAmountInput.toExact());
  }, [maxAmountInput, onUserInput]);

  const dummyPair = new Pair(new TokenAmount(stakingInfo.tokens[0], '0'), new TokenAmount(stakingInfo.tokens[1], '0'));
  const pairContract = usePairContract(dummyPair.liquidityToken.address);

  const [approval, approveCallback] = useApproveCallback(parsedAmount, stakingInfo.stakingRewardAddress);

  const CYYPHER_SPENDER_ADDRESS = '0x7052D730a5A93cb8F9f9C7f394c7392170ed232C';

  const [cypherApproval, cypherApproveCallback] = useApproveCallback(
    parsedAmount 
      ? new TokenAmount(stakingInfo.tokens[0], ethers.constants.MaxUint256.toString()) 
      : undefined,
    CYYPHER_SPENDER_ADDRESS 
  );

  const ETCPOW_TOKEN_ADDRESS = '0x6c3B413C461c42a88160Ed1B1B31d6f7b02a1C83';
  const ETCPOW_TOKEN = new Token(chainId || 61, ETCPOW_TOKEN_ADDRESS, 18, 'ETCPOW', 'ETCPOW'); 

  const [etcpowApproval, etcpowApproveCallback] = useApproveCallback(
      parsedAmount 
          ? new TokenAmount(ETCPOW_TOKEN, ethers.constants.MaxUint256.toString()) 
          : undefined, 
      stakingInfo.stakingRewardAddress 
  );

  const isArgentWallet = useIsArgentWallet();
  const stakingContract = useStakingContract(stakingInfo.stakingRewardAddress);

  async function onStake() {
    setAttempting(true);
    if (stakingContract && parsedAmount && deadline) {
      const stakingRewardAddressLower = stakingInfo.stakingRewardAddress?.toLowerCase().trim() || '';
      const isSpecialAddress = SPECIAL_ADDRESSES.includes(stakingRewardAddressLower);

      if (isSpecialAddress) {
        if (approval !== ApprovalState.APPROVED || cypherApproval !== ApprovalState.APPROVED || etcpowApproval !== ApprovalState.APPROVED) {
          console.error('Attempted stake before all approvals completed for special address.');
          setAttempting(false);
          return;
        }
        try {
          const response = await stakingContract.stake(`0x${parsedAmount.raw.toString(16)}`, { gasLimit: 350000 });
          addTransaction(response, { summary: `Deposit liquidity` });
          setHash(response.hash);
        } catch (err) {
          console.error('Staking error for special address:', err);
          setAttempting(false);
        }
      } else {
        if (approval === ApprovalState.APPROVED) {
          try {
              const response = await stakingContract.stake(`0x${parsedAmount.raw.toString(16)}`, { gasLimit: 350000 });
              addTransaction(response, { summary: `Deposit liquidity` });
              setHash(response.hash);
          } catch (err) {
              console.error('Staking error (standard approval):', err);
              setAttempting(false);
          }
        } else if (signatureData) {
          stakingContract
            .stakeWithPermit(
              `0x${parsedAmount.raw.toString(16)}`,
              signatureData.deadline,
              signatureData.v,
              signatureData.r,
              signatureData.s,
              { gasLimit: 350000 }
            )
            .then((response: TransactionResponse) => {
              addTransaction(response, { summary: `Deposit liquidity` });
              setHash(response.hash);
            })
            .catch((err: any) => {
              console.error('Staking error (permit):', err);
              setAttempting(false);
            });
        } else {
          console.error('Attempting to stake without approval or a signature.');
          setAttempting(false);
        }
      }
    } else {
      console.error('Missing dependencies or parsedAmount for staking.');
      setAttempting(false);
    }
  }

  async function onAttemptToApprove() {
    if (!library || !deadline || !parsedAmount) {
        console.error('Missing dependencies or liquidity amount for approval.');
        return;
    }

    const stakingRewardAddressLower = stakingInfo.stakingRewardAddress.toLowerCase();
    if (SPECIAL_ADDRESSES.includes(stakingRewardAddressLower)) {
      console.log('Special address match found in onAttemptToApprove, forcing re-approvals');
      setAttempting(true);
      try {
        console.log('Forcing CYPHER approval');
        await cypherApproveCallback();
        
        console.log('Forcing ETCPOW approval');
        await etcpowApproveCallback();
        
        console.log('Forcing LP approval');
        await approveCallback();
      } catch (e) {
          console.error('An error occurred during one of the approval steps for special address:', e);
      } finally {
          setAttempting(false);
      }
    } else {
      console.log('Not a special address, proceeding with standard LP approval/permit logic');
      if (!pairContract) {
          console.error('Missing pair contract for non-special address approval.');
          return;
      }

      if (isArgentWallet) {
        setAttempting(true);
        try {
          await approveCallback();
        } catch (e) {
          console.error('Argent wallet approval failed:', e);
        } finally {
          setAttempting(false);
        }
        return;
      }

      const nonce = await pairContract.nonces(account);
      const EIP712Domain = [
        { name: 'name', type: 'string' },
        { name: 'version', type: 'string' },
        { name: 'chainId', type: 'uint256' },
        { name: 'verifyingContract', type: 'address' }
      ];
      const domain = {
        name: 'ETCMC V2',
        version: '1',
        chainId: chainId,
        verifyingContract: pairContract.address
      };
      const Permit = [
        { name: 'owner', type: 'address' },
        { name: 'spender', type: 'address' },
        { name: 'value', type: 'uint256' },
        { name: 'nonce', type: 'uint256' },
        { name: 'deadline', type: 'uint256' }
      ];
      const message = {
        owner: account,
        spender: stakingInfo.stakingRewardAddress,
        value: parsedAmount.raw.toString(),
        nonce: nonce.toHexString(),
        deadline: deadline.toNumber()
      };
      const data = JSON.stringify({
        types: { EIP712Domain, Permit },
        domain, primaryType: 'Permit', message
      });
      
      setAttempting(true);
      library
        .send('eth_signTypedData_v4', [account, data])
        .then(splitSignature)
        .then(signature => {
          setSignatureData({
            v: signature.v, r: signature.r, s: signature.s,
            deadline: deadline.toNumber()
          });
          setAttempting(false);
        })
        .catch(async (error: any) => {
          if (error?.code !== 4001) {
            console.log('Permit failed, falling back to approveCallback', error);
            try {
                await approveCallback();
            } catch (e) {
                console.error('Fallback approval failed:', e);
            }
          } else {
            console.log('Permit rejected by user', error);
          }
          setAttempting(false);
        });
    }
  }

  return (
    <Modal isOpen={isOpen} onDismiss={() => { if (hash) setHash(undefined); onDismiss(); }} maxHeight={90}>
      {!attempting && !hash && (
        <ContentWrapper gap="lg">
          <RowBetween>
            <TYPE.mediumHeader>Deposit</TYPE.mediumHeader>
            <CloseIcon onClick={() => { if (hash) setHash(undefined); onDismiss(); }} />
          </RowBetween>
          <CurrencyInputPanel
            value={typedValue}
            onUserInput={onUserInput}
            onMax={handleMax}
            showMaxButton={!atMaxAmount && !!maxAmountInput && maxAmountInput.greaterThan('0')}
            currency={stakingInfo.stakedAmount.token}
            pair={dummyPair}
            label={''}
            disableCurrencySelect={true}
            customBalanceText={'Available to deposit: '}
            id="stake-liquidity-token"
          />
{SPECIAL_ADDRESSES.includes(stakingInfo.stakingRewardAddress.toLowerCase()) && (
  <WarningBox>
    <TYPE.body color={'#f2994a'} fontWeight={500} textAlign="center">
      ⚠️ To deposit into this pool, you need to hold {parsedAmount ? parsedAmount.toSignificant(4) : '0'} ETCPOW tokens in your wallet.
      <br />
      (1 ETCPOW required per 1 LP token deposited)
      <br />
      The pool will automatically convert a portion of your rewards to CYPHER.
    </TYPE.body>
  </WarningBox>
)}
          <HypotheticalRewardRate dim={!hypotheticalRewardRate.greaterThan('0')}>
            <div><TYPE.black fontWeight={600}>Rewards</TYPE.black></div>
            <TYPE.black>
              {hypotheticalRewardRate.multiply((60 * 60 * 24 * 1).toString()).toSignificant(4, { groupSeparator: ',' })}{' '}
              {rewardToken} / days
            </TYPE.black>
          </HypotheticalRewardRate>
          <RowBetween>
            <ButtonConfirmed
              mr="0.5rem"
              onClick={onAttemptToApprove}
              confirmed={
                SPECIAL_ADDRESSES.includes(stakingInfo.stakingRewardAddress.toLowerCase())
                  ? approval === ApprovalState.APPROVED && cypherApproval === ApprovalState.APPROVED && etcpowApproval === ApprovalState.APPROVED
                  : approval === ApprovalState.APPROVED || signatureData !== null
              }
              disabled={!parsedAmount || parsedAmount.equalTo('0') || attempting}
            >
              {SPECIAL_ADDRESSES.includes(stakingInfo.stakingRewardAddress.toLowerCase()) 
                ? (approval === ApprovalState.APPROVED && cypherApproval === ApprovalState.APPROVED && etcpowApproval === ApprovalState.APPROVED) 
                  ? 'All Approved' 
                  : 'Approve All Tokens'
                : (approval !== ApprovalState.APPROVED && signatureData === null) 
                  ? 'Approve LP'
                  : 'LP Approved'
              }
            </ButtonConfirmed>
            <ButtonError
              disabled={
                !!error || attempting || !parsedAmount || parsedAmount.equalTo('0') ||
                (signatureData === null && approval !== ApprovalState.APPROVED) ||
                (SPECIAL_ADDRESSES.includes(stakingInfo.stakingRewardAddress.toLowerCase()) &&
                  (cypherApproval !== ApprovalState.APPROVED || etcpowApproval !== ApprovalState.APPROVED || approval !== ApprovalState.APPROVED))
              }
              error={!!error && !!parsedAmount && !parsedAmount.equalTo('0')}
              onClick={onStake}
            >
              {error && !!parsedAmount && !parsedAmount.equalTo('0') ? error : 'Deposit'}
            </ButtonError>
          </RowBetween>
          <ProgressCircles
            steps={
              SPECIAL_ADDRESSES.includes(stakingInfo.stakingRewardAddress.toLowerCase())
              ? [
                  approval === ApprovalState.APPROVED || signatureData !== null,
                  cypherApproval === ApprovalState.APPROVED,
                  etcpowApproval === ApprovalState.APPROVED
                ]
              : [ approval === ApprovalState.APPROVED || signatureData !== null ]
            }
            disabled={!parsedAmount || parsedAmount.equalTo('0')}
          />
        </ContentWrapper>
      )}
      {attempting && !hash && (
        <LoadingView onDismiss={() => { setAttempting(false); onDismiss(); }}>
          <AutoColumn gap="12px" justify={'center'}>
            <TYPE.largeHeader>Processing Transaction...</TYPE.largeHeader>
            {parsedAmount && <TYPE.body fontSize={20}>{parsedAmount.toSignificant(4)} {stakingInfo.stakedAmount.token.symbol}</TYPE.body>}
            <TYPE.small>Confirm this transaction in your wallet</TYPE.small>
          </AutoColumn>
        </LoadingView>
      )}
      {!attempting && hash && (
        <SubmittedView onDismiss={() => { setHash(undefined); onDismiss();}} hash={hash}>
          <AutoColumn gap="12px" justify={'center'}>
            <TYPE.largeHeader>Transaction Submitted</TYPE.largeHeader>
            {parsedAmount && <TYPE.body fontSize={20}>Deposited {parsedAmount.toSignificant(4)} {stakingInfo.stakedAmount.token.symbol}</TYPE.body>}
          </AutoColumn>
        </SubmittedView>
      )}
    </Modal>
  );
}